import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { hasAnyRole } from '../utils/roles';

const emptySurveyForm = {
  title: '',
  description: '',
  category: 'genel',
  endDate: '',
  targetAudience: 'tumu',
  questions: []
};

const questionTypeOptions = [
  { value: 'text', label: 'Kısa Yanıt' },
  { value: 'multiple_choice', label: 'Çoktan Seçmeli' },
  { value: 'single_choice', label: 'Tek Seçimli' },
  { value: 'rating', label: 'Puanlama' },
  { value: 'yes_no', label: 'Evet / Hayır' }
];

const statusLabels = {
  active: 'Aktif',
  closed: 'Kapandı',
  draft: 'Taslak',
  archived: 'Arşiv'
};

const categoryLabels = {
  calisan_memnuniyeti: 'Çalışan Memnuniyeti',
  performans: 'Performans',
  egitim: 'Eğitim',
  sosyal: 'Sosyal',
  genel: 'Genel',
  proje: 'Proje'
};

const SurveyPage = () => {
  const [profile, setProfile] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptySurveyForm);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [resultsModal, setResultsModal] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canCreateSurvey = profile && hasAnyRole(profile.role, ['super_admin', 'company_admin']);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      setError('');

      const [profileResponse, surveyResponse] = await Promise.all([
        api.getProfile(),
        api.getSurveys()
      ]);

      setProfile(profileResponse.user);
      setSurveys(surveyResponse.surveys || []);
    } catch (fetchError) {
      console.error('Survey page error:', fetchError);
      setError('Anket verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  const filteredSurveys = useMemo(() => {
    if (activeFilter === 'all') {
      return surveys;
    }

    if (activeFilter === 'mine') {
      return surveys.filter((survey) => survey.author?._id === profile?._id);
    }

    if (activeFilter === 'pending') {
      return surveys.filter((survey) => survey.isOpen && !survey.hasResponded);
    }

    if (activeFilter === 'completed') {
      return surveys.filter((survey) => survey.hasResponded || survey.status === 'closed');
    }

    return surveys;
  }, [activeFilter, profile?._id, surveys]);

  const stats = useMemo(
    () => ({
      total: surveys.length,
      open: surveys.filter((survey) => survey.isOpen).length,
      waitingForMe: surveys.filter((survey) => survey.isOpen && !survey.hasResponded).length,
      completed: surveys.filter((survey) => survey.hasResponded).length
    }),
    [surveys]
  );

  const addQuestion = (questionType) => {
    setCreateForm((current) => ({
      ...current,
      questions: [
        ...current.questions,
        {
          id: Date.now(),
          questionText: '',
          questionType,
          isRequired: true,
          options:
            questionType === 'multiple_choice' || questionType === 'single_choice'
              ? ['', '']
              : []
        }
      ]
    }));
  };

  const updateQuestion = (questionId, field, value) => {
    setCreateForm((current) => ({
      ...current,
      questions: current.questions.map((question) =>
        question.id === questionId ? { ...question, [field]: value } : question
      )
    }));
  };

  const updateOption = (questionId, optionIndex, value) => {
    setCreateForm((current) => ({
      ...current,
      questions: current.questions.map((question) => {
        if (question.id !== questionId) {
          return question;
        }

        return {
          ...question,
          options: question.options.map((option, index) => (index === optionIndex ? value : option))
        };
      })
    }));
  };

  const addOption = (questionId) => {
    setCreateForm((current) => ({
      ...current,
      questions: current.questions.map((question) =>
        question.id === questionId
          ? { ...question, options: [...question.options, ''] }
          : question
      )
    }));
  };

  const removeQuestion = (questionId) => {
    setCreateForm((current) => ({
      ...current,
      questions: current.questions.filter((question) => question.id !== questionId)
    }));
  };

  const handleCreateSurvey = async (event) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      await api.createSurvey({
        ...createForm,
        questions: createForm.questions.map((question) => ({
          questionText: question.questionText,
          questionType: question.questionType,
          isRequired: question.isRequired,
          options: (question.options || []).filter(Boolean)
        }))
      });

      setShowCreateModal(false);
      setCreateForm(emptySurveyForm);
      await fetchPageData();
    } catch (createError) {
      console.error('Create survey error:', createError);
      setError('Anket oluşturulamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenSurvey = (survey) => {
    setSelectedSurvey(survey);
    setSurveyAnswers({});
  };

  const handleSubmitResponse = async () => {
    if (!selectedSurvey) {
      return;
    }

    try {
      setSubmitting(true);
      const answers = selectedSurvey.questions.map((question) => ({
        questionId: question._id,
        answer: surveyAnswers[question._id] ?? ''
      }));

      await api.submitSurveyResponse(selectedSurvey.id, { answers });
      setSelectedSurvey(null);
      setSurveyAnswers({});
      await fetchPageData();
    } catch (submitError) {
      console.error('Submit survey response error:', submitError);
      setError('Anket yanıtı kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewResults = async (survey) => {
    try {
      setResultsLoading(true);
      const response = await api.getSurveyResults(survey.id);
      setResultsModal(response.results);
    } catch (resultsError) {
      console.error('Get survey results error:', resultsError);
      setError('Anket sonuçları yüklenemedi.');
    } finally {
      setResultsLoading(false);
    }
  };

  const renderQuestionInput = (question) => {
    const value = surveyAnswers[question._id] ?? '';

    if (question.questionType === 'text') {
      return (
        <input
          className="form-control"
          type="text"
          value={value}
          onChange={(event) =>
            setSurveyAnswers((current) => ({ ...current, [question._id]: event.target.value }))
          }
        />
      );
    }

    if (question.questionType === 'rating') {
      return (
        <select
          className="form-select"
          value={value}
          onChange={(event) =>
            setSurveyAnswers((current) => ({ ...current, [question._id]: event.target.value }))
          }
        >
          <option value="">Puan seçin</option>
          {[1, 2, 3, 4, 5].map((score) => (
            <option key={score} value={score}>
              {score}
            </option>
          ))}
        </select>
      );
    }

    if (question.questionType === 'yes_no') {
      return (
        <div className="d-flex gap-3">
          {['Evet', 'Hayır'].map((option) => (
            <label key={option} className="form-check-label">
              <input
                className="form-check-input me-2"
                type="radio"
                name={question._id}
                checked={value === option}
                onChange={() =>
                  setSurveyAnswers((current) => ({ ...current, [question._id]: option }))
                }
              />
              {option}
            </label>
          ))}
        </div>
      );
    }

    if (['multiple_choice', 'single_choice'].includes(question.questionType)) {
      return (
        <div className="d-grid gap-2">
          {question.options.map((option) => (
            <label key={option.value || option.text} className="form-check-label">
              <input
                className="form-check-input me-2"
                type={question.questionType === 'single_choice' ? 'radio' : 'checkbox'}
                name={question._id}
                checked={
                  question.questionType === 'single_choice'
                    ? value === option.value
                    : Array.isArray(value) && value.includes(option.value)
                }
                onChange={(event) => {
                  if (question.questionType === 'single_choice') {
                    setSurveyAnswers((current) => ({
                      ...current,
                      [question._id]: option.value
                    }));
                    return;
                  }

                  const currentValues = Array.isArray(surveyAnswers[question._id])
                    ? surveyAnswers[question._id]
                    : [];

                  setSurveyAnswers((current) => ({
                    ...current,
                    [question._id]: event.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter((item) => item !== option.value)
                  }));
                }}
              />
              {option.text}
            </label>
          ))}
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
              <div>
                <h3 className="mb-2">Anket ve Geri Bildirim Merkezi</h3>
                <p className="text-muted mb-0">
                  Kurum içindeki anketleri takip edin, uygun olanları doldurun ve sonuç trendlerini
                  izleyin.
                </p>
              </div>
              {canCreateSurvey && (
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                  Yeni Anket Oluştur
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        {[
          ['Toplam Anket', stats.total],
          ['Aktif', stats.open],
          ['Benden Beklenen', stats.waitingForMe],
          ['Tamamlanan', stats.completed]
        ].map(([label, value]) => (
          <div key={label} className="col-md-3 col-sm-6 mb-3">
            <div className="box h-100">
              <div className="box-body">
                <div className="text-muted small">{label}</div>
                <div className="h3 mb-0">{value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-2">
            {[
              ['all', 'Tüm Anketler'],
              ['pending', 'Bekleyenler'],
              ['completed', 'Tamamlananlar'],
              ['mine', 'Oluşturduklarım']
            ].map(([value, label]) => (
              <button
                key={value}
                className={`btn ${activeFilter === value ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setActiveFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="row">
        {filteredSurveys.map((survey) => (
          <div key={survey.id} className="col-xl-4 col-md-6 mb-4">
            <div className="box h-100">
              <div className="box-body d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <span className="badge bg-light text-dark mb-2">
                      {categoryLabels[survey.category] || survey.category}
                    </span>
                    <h5 className="mb-1">{survey.title}</h5>
                  </div>
                  <span className={`badge ${survey.isOpen ? 'bg-success' : 'bg-secondary'}`}>
                    {statusLabels[survey.status] || survey.status}
                  </span>
                </div>

                <p className="text-muted flex-grow-1">{survey.description}</p>

                <div className="small text-muted mb-3">
                  <div>{survey.questionCount} soru</div>
                  <div>{survey.responseCount} yanıt</div>
                  <div>
                    Son tarih:{' '}
                    {new Date(survey.endDate).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                  <div>
                    Hazırlayan: {survey.author?.firstName} {survey.author?.lastName}
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-auto">
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!survey.isOpen || survey.hasResponded}
                    onClick={() => handleOpenSurvey(survey)}
                  >
                    {survey.hasResponded ? 'Yanıtladınız' : 'Anketi Doldur'}
                  </button>
                  {(survey.author?._id === profile?._id || canCreateSurvey) && (
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={resultsLoading}
                      onClick={() => handleViewResults(survey)}
                    >
                      Sonuçları Gör
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredSurveys.length === 0 && (
          <div className="col-12">
            <div className="box">
              <div className="box-body text-center text-muted py-5">
                Seçili filtreye uygun anket bulunmuyor.
              </div>
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <form onSubmit={handleCreateSurvey}>
                <div className="modal-header">
                  <h5 className="modal-title">Yeni Anket</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label">Başlık</label>
                      <input
                        className="form-control"
                        value={createForm.title}
                        onChange={(event) =>
                          setCreateForm((current) => ({ ...current, title: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Kategori</label>
                      <select
                        className="form-select"
                        value={createForm.category}
                        onChange={(event) =>
                          setCreateForm((current) => ({ ...current, category: event.target.value }))
                        }
                      >
                        {Object.entries(categoryLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Bitiş Tarihi</label>
                      <input
                        className="form-control"
                        type="date"
                        value={createForm.endDate}
                        onChange={(event) =>
                          setCreateForm((current) => ({ ...current, endDate: event.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Açıklama</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={createForm.description}
                        onChange={(event) =>
                          setCreateForm((current) => ({
                            ...current,
                            description: event.target.value
                          }))
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {questionTypeOptions.map((questionType) => (
                      <button
                        key={questionType.value}
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => addQuestion(questionType.value)}
                      >
                        {questionType.label}
                      </button>
                    ))}
                  </div>

                  <div className="d-grid gap-3">
                    {createForm.questions.map((question, index) => (
                      <div key={question.id} className="border rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <strong>Soru {index + 1}</strong>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeQuestion(question.id)}
                          >
                            Kaldır
                          </button>
                        </div>
                        <div className="row g-3">
                          <div className="col-md-8">
                            <label className="form-label">Soru Metni</label>
                            <input
                              className="form-control"
                              value={question.questionText}
                              onChange={(event) =>
                                updateQuestion(question.id, 'questionText', event.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Tip</label>
                            <select
                              className="form-select"
                              value={question.questionType}
                              onChange={(event) =>
                                updateQuestion(question.id, 'questionType', event.target.value)
                              }
                            >
                              {questionTypeOptions.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {(question.questionType === 'multiple_choice' ||
                            question.questionType === 'single_choice') && (
                            <div className="col-12">
                              <label className="form-label">Seçenekler</label>
                              <div className="d-grid gap-2">
                                {question.options.map((option, optionIndex) => (
                                  <input
                                    key={`${question.id}-${optionIndex}`}
                                    className="form-control"
                                    value={option}
                                    onChange={(event) =>
                                      updateOption(question.id, optionIndex, event.target.value)
                                    }
                                    placeholder={`Seçenek ${optionIndex + 1}`}
                                  />
                                ))}
                              </div>
                              <button
                                type="button"
                                className="btn btn-link px-0 mt-2"
                                onClick={() => addOption(question.id)}
                              >
                                Seçenek ekle
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting || createForm.questions.length === 0}
                  >
                    {submitting ? 'Kaydediliyor...' : 'Anketi Yayına Al'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedSurvey && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedSurvey.title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedSurvey(null)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted">{selectedSurvey.description}</p>
                <div className="d-grid gap-3">
                  {selectedSurvey.questions.map((question, index) => (
                    <div key={question._id} className="border rounded p-3">
                      <div className="fw-semibold mb-2">
                        {index + 1}. {question.questionText}
                      </div>
                      {renderQuestionInput(question)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedSurvey(null)}
                >
                  Kapat
                </button>
                <button className="btn btn-primary" onClick={handleSubmitResponse} disabled={submitting}>
                  {submitting ? 'Kaydediliyor...' : 'Yanıtı Gönder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resultsModal && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Anket Sonuçları</h5>
                <button type="button" className="btn-close" onClick={() => setResultsModal(null)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>{resultsModal.survey.title}</strong>
                  <div className="text-muted">{resultsModal.survey.responseCount} yanıt toplandı</div>
                </div>

                <div className="d-grid gap-3">
                  {resultsModal.breakdown.map((item) => (
                    <div key={item.questionId} className="border rounded p-3">
                      <div className="fw-semibold mb-2">{item.questionText}</div>
                      {item.breakdown && (
                        <div className="d-grid gap-1">
                          {Object.entries(item.breakdown).map(([option, count]) => (
                            <div key={option} className="small text-muted">
                              {option}: {count}
                            </div>
                          ))}
                        </div>
                      )}
                      {typeof item.average === 'number' && (
                        <div className="small text-muted">Ortalama puan: {item.average}</div>
                      )}
                      {item.answers && (
                        <div className="d-grid gap-1">
                          {item.answers.map((answer, index) => (
                            <div key={`${item.questionId}-${index}`} className="small text-muted">
                              {String(answer)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyPage;
